// CrazyGames SDK v3 wrappers. Every call is guarded so local `vite dev` (no SDK global)
// cleanly no-ops. Covers the lifecycle plus video ads (the revenue path).

export interface Lifecycle {
  loadingStart(): void;
  loadingStop(): void;
  gameplayStart(): void;
  gameplayStop(): void;
  /**
   * Interstitial ad at a natural break. We fire it only between matches (on "Play Again"), gated by
   * maybeInterstitial() so it never plays on the first match of a session, back-to-back with a rewarded
   * ad, or within the min interval. Each real ad brackets gameplayStop/Start and stamps the last-ad time.
   * `onStart`/`onEnd` bracket a real ad only - if none plays, neither fires.
   */
  midroll(onStart?: () => void, onEnd?: () => void): Promise<void>;
  /**
   * Opt-in rewarded ad (highest eCPM). Resolves true only if the ad actually finished, so the
   * caller grants the reward. Reserved for outcome-neutral perks (elmas/cosmetics) so it never
   * becomes pay-to-win on the deterministic engine.
   */
  rewarded(onStart?: () => void, onEnd?: () => void): Promise<boolean>;
  // --- CrazyGames account (guest-safe: guarded so local dev / logged-out / off-platform all degrade cleanly) ---
  /** True when account features work on this host, i.e. login is actually possible. */
  accountAvailable(): boolean;
  /** The logged-in CrazyGames username, or null when guest / off-platform. */
  getUsername(): string | null;
  /** The logged-in CrazyGames avatar URL, or null when guest / off-platform. */
  getAvatarUrl(): string | null;
  /** Prompt CrazyGames login; resolves true if a user is now signed in. No-op (false) when unavailable. */
  login(): Promise<boolean>;
  /** Subscribe to auth-state changes (login/logout) so UI can refresh name + avatar. */
  onUserChange(cb: () => void): void;
  // --- multiplayer social ("play with friends"; all no-op off-platform). Wires our room-code system into
  // CrazyGames' invite / room UI so the game qualifies for the Multiplayer landing page. ---
  /** Was the game launched via CrazyGames Instant Multiplayer? Boot straight into a fresh room if so. */
  isInstantMultiplayer(): boolean;
  /** The room code the player arrived with via an invite link at boot, or null. */
  joinRoomCode(): string | null;
  /** Register a callback for a mid-session invite join (the platform hands us the room code to join). */
  onJoinRoom(cb: (roomCode: string) => void): void;
  /** Show CrazyGames' invite button wired to this room, so friends can join through the platform UI. */
  showInvite(roomCode: string): void;
  /** Hide CrazyGames' invite button. */
  hideInvite(): void;
  /** Tell CrazyGames the room's joinable state (drives its join UI + the Multiplayer landing page). */
  updateRoom(roomCode: string, joinable: boolean): void;
  /** Tell CrazyGames the player left the room (clears its invite/join state). */
  leftRoom(): void;
}

// the invite-link param that carries our room code between the platform and the game
const ROOM_PARAM = 'room';

const AD_SAFETY_MS = 60000; // real ads are <=~30s; this only trips if the SDK goes silent, so the turn never hangs

// True only after SDK.init() actually SUCCEEDED this session (decided before main() touches storage,
// and never changes afterwards - a late-completing init must not flip anything mid-session).
let sdkReady = false;
export const isSdkReady = (): boolean => sdkReady;
/** Whether the CrazyGames data store is the persistence backend. Only on REAL CrazyGames hosts: on our
 *  own mirrors the SDK sometimes manages to init and sometimes hangs (it varies by day and domain), and
 *  letting that race pick the backend would hide a returning player's localStorage progress whenever it
 *  flipped. On-platform the SDK store is what survives the iframe and follows the account. */
export const useCgStore = (): boolean => sdkReady && window.location.hostname.includes('crazygames');

function noopLifecycle(): Lifecycle {
  const noop = (): void => {};
  return {
    loadingStart: noop,
    loadingStop: noop,
    gameplayStart: noop,
    gameplayStop: noop,
    midroll: () => Promise.resolve(),
    rewarded: () => Promise.resolve(false),
    accountAvailable: () => false,
    getUsername: () => null,
    getAvatarUrl: () => null,
    login: () => Promise.resolve(false),
    onUserChange: noop,
    isInstantMultiplayer: () => false,
    joinRoomCode: () => null,
    onJoinRoom: noop,
    showInvite: noop,
    hideInvite: noop,
    updateRoom: noop,
    leftRoom: noop,
  };
}

// --- ad pacing across the Play Again reload. These live in sessionStorage (NOT a module variable),
// because "Play Again" does window.location.reload() and a closure var would reset every match. ---
const AD_LAST_KEY = 'wi.session.lastAdAt';
const MATCH_COUNT_KEY = 'wi.session.matches';
/** CrazyGames asks for a gap between interstitials; keep them at least this far apart. */
export const MIN_AD_INTERVAL_MS = 3 * 60 * 1000;

function stampLastAd(): void {
  try {
    sessionStorage.setItem(AD_LAST_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable - pacing simply falls back to "allow" */
  }
}

/** Milliseconds since the last ad of ANY kind this session, or Infinity if none has played yet. */
export function msSinceLastAd(): number {
  try {
    const t = Number(sessionStorage.getItem(AD_LAST_KEY));
    return t > 0 ? Date.now() - t : Infinity;
  } catch {
    return Infinity;
  }
}

/** Call when a real match begins; returns the running count (1 = first match of the session). */
export function noteMatchStarted(): number {
  try {
    const n = (Number(sessionStorage.getItem(MATCH_COUNT_KEY)) || 0) + 1;
    sessionStorage.setItem(MATCH_COUNT_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}

/** How many real matches have started this session (0 before the first). */
export function sessionMatchCount(): number {
  try {
    return Number(sessionStorage.getItem(MATCH_COUNT_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * The Play-Again interstitial gate: show it only when this is NOT the first match of the session, enough
 * time has passed since the last ad, and the player did not just watch a rewarded ad on this results
 * screen (no two ads back-to-back). Keeps the ad cadence natural (see docs/META-ECONOMY.md section 8).
 */
export function shouldShowInterstitial(rewardedJustWatched: boolean): boolean {
  return !rewardedJustWatched && sessionMatchCount() > 1 && msSinceLastAd() >= MIN_AD_INTERVAL_MS;
}

/** Resolves after SDK.init() (or immediately if the SDK is absent). */
export async function initPlatform(): Promise<Lifecycle> {
  const sdk = window.CrazyGames?.SDK;
  if (!sdk) return noopLifecycle();
  try {
    // On unknown domains (a staging host like pages.dev) SDK.init can HANG instead of rejecting;
    // never let the platform layer block the game boot: a race and we play on without it. On the real
    // CrazyGames domains the SDK is expected to init, so it gets a much longer leash - tripping the
    // timeout THERE would silently disable ads and lifecycle reporting for the whole session.
    const initTimeoutMs = window.location.hostname.includes('crazygames') ? 12000 : 3000;
    await Promise.race([sdk.init(), new Promise((_, reject) => window.setTimeout(() => reject(new Error('sdk init timeout')), initTimeoutMs))]);
    sdkReady = true; // from here on the CG data store is the storage backend for this session
  } catch {
    return noopLifecycle();
  }
  const guard =
    (fn: () => void): (() => void) =>
    () => {
      try {
        fn();
      } catch {
        /* SDK call failed - non-fatal */
      }
    };

  // The SDK's view of whether gameplay is running, mirrored by the lifecycle wrappers below so an ad
  // knows what to restore: an ad requested from the game-over screen must NOT report gameplayStart
  // when it ends (the player is still on the results screen, not playing).
  let inGameplay = false;
  // Request an ad and resolve once it ends. `grantOnFinish` distinguishes rewarded (reward only on a
  // clean finish) from midgame (we just need to continue whether it played or not).
  const requestAd = (adType: 'midgame' | 'rewarded', onStart?: () => void, onEnd?: () => void): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      let settled = false;
      let started = false;
      let resumeAfter = false; // was gameplay running when the ad interrupted it?
      const settle = (granted: boolean): void => {
        if (settled) return;
        settled = true;
        if (started) {
          try {
            onEnd?.();
          } catch {
            /* ignore */
          }
          if (resumeAfter) {
            try {
              sdk.game.gameplayStart(); // CrazyGames guidance: resume gameplay after the ad
              inGameplay = true;
            } catch {
              /* ignore */
            }
          }
        }
        resolve(granted);
      };
      try {
        sdk.ad.requestAd(adType, {
          adStarted: () => {
            started = true;
            resumeAfter = inGameplay;
            try {
              sdk.game.gameplayStop(); // pause gameplay while a real ad plays
              inGameplay = false;
            } catch {
              /* ignore */
            }
            stampLastAd(); // record "an ad of ANY kind just played" for the interstitial interval gate
            try {
              onStart?.();
            } catch {
              /* ignore */
            }
          },
          adFinished: () => settle(true),
          adError: () => settle(false),
        });
      } catch {
        settle(false);
      }
      // never let a silent SDK freeze the game
      window.setTimeout(() => settle(false), AD_SAFETY_MS);
    });

  // --- CrazyGames account: read the logged-in user (if any) and keep it fresh. Everything is guarded so a
  // missing user module, a logged-out player, or an off-platform host all degrade to "guest" cleanly. ---
  let username: string | null = null;
  let avatarUrl: string | null = null;
  let accountAvail = false;
  const userChangeCbs: Array<() => void> = [];
  const applyUser = (u: CrazyGamesUser | null): void => {
    username = u?.username ?? null;
    avatarUrl = u?.profilePictureUrl ?? null;
    for (const cb of userChangeCbs) {
      try {
        cb();
      } catch {
        /* ignore */
      }
    }
  };
  const sandbox = (sdk as unknown as { environment?: string }).environment === 'local'; // dev sandbox fakes a "User1"
  // fire-and-forget: the account round-trips have no timeout, so awaiting them here would put a stallable
  // network call on every boot's path to gameplayStart. Getters serve guest values until this resolves;
  // applyUser notifies the onUserChange subscribers (Profile/Shop) whenever the real account lands. The
  // listener registers synchronously so it exists even if the account service never answers.
  const user = sdk.user;
  if (user) {
    try {
      user.addAuthListener((u) => applyUser(u));
    } catch {
      /* older SDK without auth listeners */
    }
    void (async () => {
      try {
        accountAvail = !!(await Promise.resolve(user.isUserAccountAvailable));
      } catch {
        accountAvail = false;
      }
      try {
        if (!sandbox) applyUser(await user.getUser()); // sandbox: stay a guest so the unique guest name shows
      } catch {
        /* not logged in */
      }
    })();
  }

  return {
    loadingStart: guard(() => sdk.game.loadingStart()),
    loadingStop: guard(() => sdk.game.loadingStop()),
    gameplayStart: guard(() => {
      inGameplay = true;
      sdk.game.gameplayStart();
    }),
    gameplayStop: guard(() => {
      inGameplay = false;
      sdk.game.gameplayStop();
    }),
    midroll: (onStart, onEnd) => requestAd('midgame', onStart, onEnd).then(() => undefined),
    rewarded: (onStart, onEnd) => requestAd('rewarded', onStart, onEnd),
    accountAvailable: () => accountAvail,
    getUsername: () => username,
    getAvatarUrl: () => avatarUrl,
    login: async () => {
      if (!sdk.user || !accountAvail) return false;
      try {
        const u = await sdk.user.showAuthPrompt();
        applyUser(u);
        return !!u;
      } catch {
        return false;
      }
    },
    onUserChange: (cb) => {
      userChangeCbs.push(cb);
    },
    // --- multiplayer social: our room CODE rides in the invite param `room`. Every call is guarded so a
    // build without the game-module multiplayer methods (older SDK / embed) degrades to a no-op. ---
    isInstantMultiplayer: () => {
      try {
        return !!sdk.game.isInstantMultiplayer;
      } catch {
        return false;
      }
    },
    joinRoomCode: () => {
      try {
        return sdk.game.getInviteParam?.(ROOM_PARAM) ?? sdk.game.inviteParams?.[ROOM_PARAM] ?? null;
      } catch {
        return null;
      }
    },
    onJoinRoom: (cb) => {
      try {
        sdk.game.addJoinRoomListener?.((params) => {
          const code = params?.[ROOM_PARAM];
          if (code) cb(code);
        });
      } catch {
        /* older SDK without the join listener */
      }
    },
    showInvite: (roomCode) => {
      try {
        sdk.game.showInviteButton?.({ [ROOM_PARAM]: roomCode });
      } catch {
        /* ignore */
      }
    },
    hideInvite: guard(() => sdk.game.hideInviteButton?.()),
    updateRoom: (roomCode, joinable) => {
      try {
        sdk.game.updateRoom?.({ roomId: roomCode, isJoinable: joinable, inviteParams: { [ROOM_PARAM]: roomCode } });
      } catch {
        /* ignore */
      }
    },
    leftRoom: guard(() => sdk.game.leftRoom?.()),
  };
}
